import { useEffect } from "react"
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom"
import { Toaster } from "sonner"
import { Menu } from "lucide-react"
import { AppSidebarProvider, Sidebar } from "@/components/Sidebar"
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar"
import { NotificationsBell } from "@/components/NotificationsBell"
import { ActiveJobsTray } from "@/components/ActiveJobsTray"
import { WhatsNewButton } from "@/components/help/WhatsNewButton"
import { BidMenu } from "@/components/bid-menu/BidMenu"
import { AssistantDock, AssistantDockToggle } from "@/components/assistant-dock/AssistantDock"
import { DocumentPreviewModal } from "@/components/ui/document-preview-modal"
import { FeedbackDialog } from "@/components/FeedbackDialog"
import { MarkAsSubmittedDialog } from "@/components/harvest/MarkAsSubmittedDialog"
import { OutcomeDialog } from "@/components/harvest/OutcomeDialog"
import { RestrictedHarvestWarningDialog } from "@/components/harvest/RestrictedHarvestWarningDialog"
import { useBidsStore } from "@/stores/bids-store"
import { useResponsesStore } from "@/stores/responses-store"
import { useDraftingStore } from "@/stores/drafting-store"
import { useActiveJobsStore } from "@/stores/active-jobs-store"
import { useAssistantStore } from "@/stores/assistant-store"
import { assistantThreadTitle } from "@/lib/assistant-thread-title"
import { pageTitles, getPageTitle, getBidSectionTitle } from "@/components/layouts/page-titles"
import { DocumentTitle } from "@/components/layouts/DocumentTitle"
import {
  Breadcrumb,
  BreadcrumbCurrent,
  BreadcrumbHome,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@/components/layouts/breadcrumb"
import { myWorkReturnPath } from "@/lib/my-work-row"
import { KNOWLEDGE_BASE_PATH } from "@/components/knowledge/knowledge-tabs"
import { TemplateIssueHost } from "@/components/TemplateIssueDialog"

// Context type for passing data to child routes (kept for potential future use)
export interface BidDetailsContext {
  // Currently empty - section is derived from URL
}


export function HeaderNavigation() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ bidId?: string; artifactId?: string; sectionKey?: string; threadId?: string }>()
  const { selectedBid } = useBidsStore()
  const { selectedArtifact } = useResponsesStore()
  const outline = useDraftingStore((s) => s.outline)
  const isAssistantThreadPage = location.pathname.startsWith("/app/assistant/")
  const assistantThreadId = isAssistantThreadPage ? params.threadId : undefined
  // Null until the conversation list has loaded (the crumb shows "..." then).
  const assistantThreadLabel = useAssistantStore((s) => {
    if (!assistantThreadId) return null
    const thread = s.threads.find((t) => t.id === assistantThreadId)
    return thread ? assistantThreadTitle(thread.name, s.threadPreviews[thread.id]) : null
  })

  // Determine page type from route
  const { bidId, artifactId, sectionKey } = params
  const isBidPage = bidId !== undefined
  const isResponsesPage = location.pathname.includes('/responses')
  const isHomePage = location.pathname === "/app"

  // Home page — just "Home" text
  if (isHomePage) {
    return (
      <div className="flex items-center text-sm">
        <span className="font-medium">Home</span>
      </div>
    )
  }

  if (artifactId && isResponsesPage) {
    const isQuestionnaire = selectedArtifact?.artifactType === 'questionnaire'
    // Section title for section pages
    const isSectionPage = sectionKey !== undefined
    const sectionTitle = isSectionPage
      ? outline?.sections.find((s) => s.sectionKey === sectionKey)?.title
        ?? (outline?.sections.find((s) => s.sectionKey === sectionKey) as Record<string, unknown> | undefined)?.['prompt'] as string | undefined
      : undefined

    // Artifact detail URL — questionnaires go to /questionnaire, others to the artifact page
    const artifactUrl = isQuestionnaire
      ? `/app/bids/${bidId}/responses/${artifactId}/questionnaire`
      : `/app/bids/${bidId}/responses/${artifactId}`

    // A section opened from My Work leads back there instead of to Bids.
    const myWorkPath = isSectionPage ? myWorkReturnPath(location.state) : null

    // Artifact/Section pages: Home > Bids (or My Work) > [Bid] > Write > [Artifact] (> [Section])
    return (
      <Breadcrumb>
        <BreadcrumbHome onClick={() => void navigate("/app")} />
        <BreadcrumbSeparator />
        {myWorkPath ? (
          <BreadcrumbLink label="Needs you" onClick={() => void navigate(myWorkPath)} />
        ) : (
          <BreadcrumbLink label="Bids" onClick={() => void navigate("/app/bids")} />
        )}
        <BreadcrumbSeparator />
        <BreadcrumbLink
          label={selectedBid?.title ?? "..."}
          onClick={() => void navigate(`/app/bids/${bidId}`)}
          dynamic
        />
        <BreadcrumbSeparator />
        <BreadcrumbLink
          label="Write"
          onClick={() => void navigate(`/app/bids/${bidId}/write`)}
        />
        <BreadcrumbSeparator />
        {isSectionPage ? (
          <>
            <BreadcrumbLink
              label={selectedArtifact?.name ?? "..."}
              onClick={() => void navigate(artifactUrl)}
              dynamic
            />
            <BreadcrumbSeparator />
            <BreadcrumbCurrent label={sectionTitle ?? "..."} dynamic />
          </>
        ) : (
          <BreadcrumbCurrent label={selectedArtifact?.name ?? "..."} dynamic />
        )}
      </Breadcrumb>
    )
  }

  if (isBidPage) {
    // Bid pages: Home > Bids > [Bid] (> [Section], e.g. Qualify)
    const sectionTitle = getBidSectionTitle(location.pathname)
    return (
      <Breadcrumb>
        <BreadcrumbHome onClick={() => void navigate("/app")} />
        <BreadcrumbSeparator />
        <BreadcrumbLink label="Bids" onClick={() => void navigate("/app/bids")} />
        <BreadcrumbSeparator />
        {sectionTitle ? (
          <>
            <BreadcrumbLink
              label={selectedBid?.title ?? "..."}
              onClick={() => void navigate(`/app/bids/${bidId}`)}
              dynamic
            />
            <BreadcrumbSeparator />
            <BreadcrumbCurrent label={sectionTitle} className="font-medium" />
          </>
        ) : (
          <BreadcrumbCurrent label={selectedBid?.title ?? "..."} dynamic />
        )}
      </Breadcrumb>
    )
  }

  // Knowledge tabs: Home > Knowledge > Tab
  if (location.pathname.startsWith(`${KNOWLEDGE_BASE_PATH}/`)) {
    return (
      <Breadcrumb>
        <BreadcrumbHome onClick={() => void navigate("/app")} />
        <BreadcrumbSeparator />
        <BreadcrumbLink label="Knowledge" onClick={() => void navigate(KNOWLEDGE_BASE_PATH)} />
        <BreadcrumbSeparator />
        <BreadcrumbCurrent label={getPageTitle(location.pathname)} className="font-medium" />
      </Breadcrumb>
    )
  }

  // An open Assistant conversation: Home > Assistant > [Conversation]
  if (assistantThreadId) {
    return (
      <Breadcrumb>
        <BreadcrumbHome onClick={() => void navigate("/app")} />
        <BreadcrumbSeparator />
        <BreadcrumbLink label="Assistant" onClick={() => void navigate("/app/assistant")} />
        <BreadcrumbSeparator />
        <BreadcrumbCurrent label={assistantThreadLabel ?? "..."} dynamic />
      </Breadcrumb>
    )
  }

  // Admin sub-pages: Home > Admin > Page Title
  const isAdminPage = location.pathname.startsWith("/app/admin")
  const isAdminSubPage = isAdminPage && location.pathname !== "/app/admin"
  if (isAdminSubPage) {
    const pageTitle = pageTitles[location.pathname] ?? "Admin"
    return (
      <Breadcrumb>
        <BreadcrumbHome onClick={() => void navigate("/app")} />
        <BreadcrumbSeparator />
        <BreadcrumbLink label="Admin" onClick={() => void navigate("/app/admin")} />
        <BreadcrumbSeparator />
        <BreadcrumbCurrent label={pageTitle} className="font-medium" />
      </Breadcrumb>
    )
  }

  // List pages: Home > Page Title
  const pageTitle = getPageTitle(location.pathname)
  return (
    <Breadcrumb>
      <BreadcrumbHome onClick={() => void navigate("/app")} />
      <BreadcrumbSeparator />
      <BreadcrumbCurrent label={pageTitle} className="font-medium" />
    </Breadcrumb>
  )
}

export function AppLayout() {
  const params = useParams<{ bidId?: string; artifactId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const startActiveJobsPolling = useActiveJobsStore((s) => s.startPolling)
  const stopActiveJobsPolling = useActiveJobsStore((s) => s.stopPolling)

  // No Cmd/Ctrl+B sidebar shortcut: it would steal Bold from the TipTap editor.

  // R12 — MFA step-up listener. Backend returns 403 + code='MFA_REQUIRED' for
  // sensitive actions; api.ts dispatches this window event so we can navigate
  // to the challenge page without forcing a full reload.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ returnTo: string }>).detail
      const returnTo = detail?.returnTo ?? window.location.pathname + window.location.search
      void navigate(`/mfa-challenge?stepUp=1&returnTo=${encodeURIComponent(returnTo)}`)
    }
    window.addEventListener('mfa-step-up-required', handler)
    return () => window.removeEventListener('mfa-step-up-required', handler)
  }, [navigate])

  useEffect(() => {
    startActiveJobsPolling()
    return () => stopActiveJobsPolling()
  }, [startActiveJobsPolling, stopActiveJobsPolling])

  // SharePoint OAuth result toast — global handler. After the callback, the
  // backend 302s the user back to
  // `${returnTo}?sharepoint=connected|denied|expired|error`.
  // Read the param on every navigation, fire a toast, then strip the param
  // from the URL so refresh / share doesn't replay the toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const status = params.get('sharepoint')
    if (!status) return

    void import('sonner').then(({ toast }) => {
      if (status === 'connected') {
        toast.success('SharePoint connected')
      } else if (status === 'denied') {
        toast.info('SharePoint connection cancelled')
      } else if (status === 'expired') {
        // Benign: the consent screen sat open past the 10-minute window.
        // Not a failure — say what to do instead of implying a fault.
        toast.info('SharePoint sign-in timed out — please connect again')
      } else if (status === 'error') {
        toast.error('SharePoint connection failed — please try again')
      }
    })

    params.delete('sharepoint')
    const clean = params.toString()
    void navigate(
      { pathname: location.pathname, search: clean ? `?${clean}` : '' },
      { replace: true }
    )
  }, [location.search, location.pathname, navigate])

  // Context to pass to BidDetailsPage via Outlet (section now derived from URL)
  const bidDetailsContext: BidDetailsContext = {}

  return (
    <AppSidebarProvider className="h-screen overflow-hidden bg-background">
      {/* Sole owner of document.title for every authenticated route */}
      <DocumentTitle />

      {/* App rail — always narrow; its own border-r is the single seam against the content */}
      <Sidebar />

      {/* Right side: full-bleed content area */}
      <SidebarInset className="overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden bg-background">
          {/* Header - shows page title or breadcrumb */}
          <header className="bg-card border-b border-border z-20">
            <div className="flex items-center gap-2 px-6 py-2 h-12">
              {/* Mobile only: opens the sidebar as a sheet */}
              <SidebarTrigger className="h-8 w-8 shrink-0 md:hidden" aria-label="Open navigation menu">
                <Menu className="h-5 w-5" />
              </SidebarTrigger>
              <HeaderNavigation />
              <div className="ml-auto flex shrink-0 items-center gap-2">
                <ActiveJobsTray />
                <WhatsNewButton />
                <NotificationsBell />
                <AssistantDockToggle />
              </div>
            </div>
          </header>

          {/* Main Content. Bid pages (editors included) get the bid menu on the
              left; the assistant dock sits on the right (it docks when the page keeps
              >=720px beside it and overlays the page otherwise, hence `relative`). */}
          <div className="relative flex flex-1 overflow-hidden bg-background">
            {params.bidId && <BidMenu bidId={params.bidId} />}
            <div className="min-w-0 flex-1 overflow-hidden">
              <Outlet context={bidDetailsContext} />
            </div>
            <AssistantDock />
          </div>
        </div>
      </SidebarInset>

      {/* Global document preview modal */}
      <DocumentPreviewModal />

      {/* Global feedback dialog — opened from the rail's avatar menu */}
      <FeedbackDialog />

      {/* Status-transition dialogs: submission harvest + outcome capture */}
      <MarkAsSubmittedDialog />
      <OutcomeDialog />
      <RestrictedHarvestWarningDialog />

      {/* Template check warning for a refused export, from any export door (R3 D4) */}
      <TemplateIssueHost />

      {/* Toast notifications */}
      <Toaster position="bottom-right" richColors closeButton />
    </AppSidebarProvider>
  )
}
